/**
 * R158d · Feb 2026 — Watch List header button + popup.
 *
 * A per-user, cross-project list of tasks the user wants to keep tabs on.
 * Fires a `acq:watch-refresh` event whenever a task is added or removed
 * so any open WatchListPopup, ProjectCard task row, or TaskDetail header
 * icon can re-sync instantly.
 */
import { useCallback, useEffect, useState } from "react";
import { Eye, ExternalLink, X, Trash2, Search, Edit3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { TaskDetail } from "@/pages/app/Tasks";
import TaskHoverCard from "@/components/tasks/TaskHoverCard";
import PriorityIcon from "@/components/tasks/PriorityIcon";

const PRIORITY_CLASS = {
  urgent: "text-rose-600",
  high: "text-amber-600",
  medium: "text-neutral-600",
  low: "text-neutral-400",
};

const STATUS_DOT = {
  open: "bg-neutral-400",
  in_progress: "bg-amber-400",
  blocked: "bg-rose-500",
  done: "bg-emerald-500",
};

export function refreshWatchList() {
  try { window.dispatchEvent(new CustomEvent("acq:watch-refresh")); } catch { /* ignore */ }
}

export async function toggleWatch(wsId, taskId, currentlyWatched) {
  if (!wsId || !taskId) return currentlyWatched;
  try {
    if (currentlyWatched) {
      await api.delete(`/workspaces/${wsId}/task-watch/${taskId}`);
      toast.success("Removed from watch list");
    } else {
      await api.post(`/workspaces/${wsId}/task-watch/${taskId}`);
      toast.success("Added to watch list");
    }
    refreshWatchList();
    return !currentlyWatched;
  } catch (e) {
    toast.error(apiErrorMessage(e, "Watch toggle failed"));
    return currentlyWatched;
  }
}

// R158h · Feb 2026 — Symmetric helper for projects. Fires the same
// `acq:watch-refresh` event so the header badge, Planning Board card
// icons, and Project header watch pill all resync in one shot.
export async function toggleProjectWatch(wsId, projectId, currentlyWatched) {
  if (!wsId || !projectId) return currentlyWatched;
  try {
    if (currentlyWatched) {
      await api.delete(`/workspaces/${wsId}/project-watch/${projectId}`);
      toast.success("Project removed from watch list");
    } else {
      await api.post(`/workspaces/${wsId}/project-watch/${projectId}`);
      toast.success("Project added to watch list");
    }
    refreshWatchList();
    return !currentlyWatched;
  } catch (e) {
    toast.error(apiErrorMessage(e, "Project watch toggle failed"));
    return currentlyWatched;
  }
}

/** Header button (shows count badge + opens the popup). */
export default function WatchListHeaderButton() {
  const { activeWorkspaceId } = useAuth();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    if (!activeWorkspaceId) { setCount(0); return; }
    try {
      const [t, p] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/task-watch/ids`),
        api.get(`/workspaces/${activeWorkspaceId}/project-watch/ids`).catch(() => ({ data: { project_ids: [] } })),
      ]);
      const tc = (t.data?.task_ids || []).length;
      const pc = (p.data?.project_ids || []).length;
      setCount(tc + pc);
    } catch { /* silent */ }
  }, [activeWorkspaceId]);

  useEffect(() => { loadCount(); }, [loadCount]);

  useEffect(() => {
    const h = () => loadCount();
    window.addEventListener("acq:watch-refresh", h);
    return () => window.removeEventListener("acq:watch-refresh", h);
  }, [loadCount]);

  return (
    <>
      <button
        type="button"
        title="Watch list"
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center justify-center h-8 w-8 rounded hover:bg-muted"
        data-testid="header-watch-list-btn"
      >
        <Eye className="w-4 h-4" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-none flex items-center justify-center"
            data-testid="header-watch-list-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && <WatchListPopup onClose={() => setOpen(false)} />}
    </>
  );
}

/** Popup listing watched tasks + projects with actions. */
export function WatchListPopup({ onClose }) {
  const { activeWorkspaceId, user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("tasks");
  const [rows, setRows] = useState([]);
  const [projectRows, setProjectRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [detailTask, setDetailTask] = useState(null);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [{ data: td }, { data: pd }, m] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/task-watch`),
        api.get(`/workspaces/${activeWorkspaceId}/project-watch`).catch(() => ({ data: { rows: [] } })),
        api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
      ]);
      setRows(td?.rows || []);
      setProjectRows(pd?.rows || []);
      setMembers(m.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load watch list")); }
    finally { setBusy(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("acq:watch-refresh", h);
    return () => window.removeEventListener("acq:watch-refresh", h);
  }, [load]);

  const removeOne = async (tid) => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/task-watch/${tid}`);
      setRows((prev) => prev.filter((r) => r.id !== tid));
      refreshWatchList();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  const openTaskInline = async (task) => {
    try {
      const { data } = await api.get(`/tasks/${task.id}`);
      setDetailTask(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load task")); }
  };

  const openProjectTab = (task) => {
    if (!task.project_id) { toast.info("Task has no project"); return; }
    window.open(`/app/projects/${task.project_id}`, "_blank");
  };

  const patchTaskInline = async (id, patch) => {
    const { data } = await api.patch(`/tasks/${id}`, patch);
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    return data;
  };

  const filteredTasks = q.trim()
    ? rows.filter((t) =>
        (t.title || "").toLowerCase().includes(q.toLowerCase()) ||
        (t.project_name || "").toLowerCase().includes(q.toLowerCase()))
    : rows;

  const filteredProjects = q.trim()
    ? projectRows.filter((p) =>
        (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
        (p.client_name || "").toLowerCase().includes(q.toLowerCase()) ||
        (p.brand_name || "").toLowerCase().includes(q.toLowerCase()))
    : projectRows;

  const removeProject = async (pid) => {
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/project-watch/${pid}`);
      setProjectRows((prev) => prev.filter((p) => p.id !== pid));
      refreshWatchList();
    } catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
  };

  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent
          className="max-w-3xl w-[95vw] max-h-[85vh] p-0 rounded-none flex flex-col"
          data-testid="watch-list-dialog"
        >
          <DialogHeader className="px-4 py-3 border-b border-border shrink-0">
            <DialogTitle className="text-sm inline-flex items-center gap-2">
              <Eye className="w-4 h-4" /> Watch list
            </DialogTitle>
            <DialogDescription className="sr-only">Cross-project list of tasks and projects you are watching.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center border-b border-border shrink-0">
            <button
              onClick={() => setTab("tasks")}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-medium ${tab === "tasks" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="watch-list-tab-tasks"
            >
              Tasks · {rows.length}
            </button>
            <button
              onClick={() => setTab("projects")}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-medium ${tab === "projects" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="watch-list-tab-projects"
            >
              Projects · {projectRows.length}
            </button>
            <button
              onClick={() => setTab("prefs")}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-medium ml-auto ${tab === "prefs" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="watch-list-tab-prefs"
              title="Choose which changes trigger a notification"
            >
              Preferences
            </button>
          </div>

          {tab !== "prefs" && (
          <div className="px-4 py-2 border-b border-border shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tab === "tasks" ? "Filter by task or project…" : "Filter by project, client or brand…"}
                className="pl-8 h-9 text-sm rounded-none"
                data-testid="watch-list-search"
              />
            </div>
          </div>
          )}

          <div className="flex-1 overflow-y-auto">
            {busy ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
            ) : tab === "prefs" ? (
              <WatchPrefsPanel />
            ) : tab === "projects" ? (
              filteredProjects.length === 0 ? (
                <div className="p-8 text-center text-xs text-muted-foreground italic">
                  {projectRows.length === 0
                    ? "Nothing watched yet. Add projects from the Planning Board or Project detail."
                    : "No projects match this filter."}
                </div>
              ) : (
                <ul className="divide-y">
                  {filteredProjects.map((p) => (
                    <li
                      key={p.id}
                      className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/40"
                      data-testid={`watch-list-project-row-${p.id}`}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0 bg-emerald-500" title={p.health || "healthy"} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {[p.client_name, p.brand_name, p.pm_name || p.owner_name].filter(Boolean).join(" · ") || "—"}
                          {typeof p.progress_percent === "number" ? ` · ${p.progress_percent}%` : ""}
                        </div>
                      </div>
                      <Button
                        size="sm" variant="outline"
                        className="rounded-none h-7 text-[10px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `/app/projects/${p.id}`;
                          const w = window.open(url, "_blank", "noopener,noreferrer");
                          if (!w) { window.location.href = url; }
                        }}
                        title="Open project in new tab"
                        data-testid={`watch-list-project-open-${p.id}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" /> Open
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="rounded-none h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={(e) => { e.stopPropagation(); removeProject(p.id); }}
                        title="Remove from watch list"
                        data-testid={`watch-list-project-remove-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )
            ) : filteredTasks.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground italic">
                {rows.length === 0
                  ? "Nothing watched yet. Add tasks from the Planning Board or Task detail."
                  : "No tasks match this filter."}
              </div>
            ) : (
              <ul className="divide-y">
                {filteredTasks.map((t) => (
                  <TaskHoverCard key={t.id} task={t} users={[]} side="right" align="center">
                    <li
                      className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/40"
                      data-testid={`watch-list-row-${t.id}`}
                    >
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[t.status] || "bg-neutral-400"}`}
                      title={t.status}
                    />
                    <PriorityIcon priority={t.priority || "medium"} className="w-3.5 h-3.5" />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                        {t.title}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {t.project_name || "—"}
                        {t.due_date ? ` · due ${t.due_date.slice(0, 10)}` : ""}
                      </div>
                    </div>
                    <Button
                      size="sm" variant="outline"
                      className="rounded-none h-7 text-[10px]"
                      onClick={(e) => { e.stopPropagation(); openTaskInline(t); }}
                      title="Edit task"
                      data-testid={`watch-list-open-${t.id}`}
                    >
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="rounded-none h-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!t.project_id) { toast.info("Task has no project"); return; }
                        const url = `/app/projects/${t.project_id}`;
                        const w = window.open(url, "_blank", "noopener,noreferrer");
                        if (!w) { window.location.href = url; }
                      }}
                      title="Open project in new tab"
                      data-testid={`watch-list-open-project-${t.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="rounded-none h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={(e) => { e.stopPropagation(); removeOne(t.id); }}
                      title="Remove from watch list"
                      data-testid={`watch-list-remove-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                  </TaskHoverCard>
                ))}
              </ul>
            )}
          </div>

          <div className="px-4 py-2 border-t border-border text-[11px] text-muted-foreground shrink-0 flex items-center justify-between">
            <span>Click a task to edit · hover row for open/remove actions</span>
            <button onClick={onClose} className="hover:bg-muted p-1 rounded" data-testid="watch-list-close"><X className="w-3.5 h-3.5" /></button>
          </div>
        </DialogContent>
      </Dialog>

      {detailTask && (
        <TaskDetail
          task={detailTask}
          members={members}
          allTasks={rows}
          onClose={() => setDetailTask(null)}
          onPatch={(patch) => patchTaskInline(detailTask.id, patch)}
          onArchiveChanged={async () => {
            try {
              const { data } = await api.get(`/tasks/${detailTask.id}`);
              if (data && data.id) setDetailTask(data);
              load();
            } catch { /* ignore */ }
          }}
          currentUserId={user?.id}
        />
      )}
    </>
  );
}


// R158z · Feb 2026 — Watch preferences panel. Lets the user opt-in to
// notifications on additional task fields beyond the default status +
// dates triggers. Server-side handled by /users/me/watch-prefs; the
// diff_and_notify pipeline consults each watcher's prefs before pinging.
const PREF_GROUPS = [
  { k: "status",      label: "Status changes",      hint: "Open → In progress → Blocked → Done",     locked: false },
  { k: "dates",       label: "Date changes",        hint: "Due, Start, Actual start / end",          locked: false },
  { k: "priority",    label: "Priority changes",    hint: "Low → Medium → High → Urgent",             locked: false },
  { k: "assignee",    label: "Assignee changes",    hint: "When the task is reassigned to someone else", locked: false },
  { k: "attachments", label: "Attachment activity", hint: "When a file is uploaded or removed",       locked: false },
];

function WatchPrefsPanel() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/users/me/watch-prefs")
      .then(({ data }) => setPrefs(data?.prefs || {}))
      .catch(() => setPrefs({ status: true, dates: true, priority: false, assignee: false, attachments: false }));
  }, []);

  const toggle = async (k, v) => {
    if (busy || !prefs) return;
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    setBusy(true);
    try {
      await api.patch("/users/me/watch-prefs", { prefs: next });
      toast.success("Watch preferences saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
      // Roll back
      setPrefs(prefs);
    } finally { setBusy(false); }
  };

  if (!prefs) {
    return <div className="p-8 text-center text-xs text-muted-foreground">Loading preferences…</div>;
  }

  return (
    <div className="p-4 space-y-1" data-testid="watch-prefs-panel">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
        Ping me when a watched task changes…
      </div>
      {PREF_GROUPS.map((g) => (
        <label
          key={g.k}
          className="flex items-start gap-3 p-2.5 border border-border cursor-pointer hover:bg-muted/40"
          data-testid={`watch-prefs-row-${g.k}`}
        >
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4"
            checked={!!prefs[g.k]}
            onChange={(e) => toggle(g.k, e.target.checked)}
            disabled={busy}
            data-testid={`watch-prefs-toggle-${g.k}`}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{g.label}</div>
            <div className="text-[11px] text-muted-foreground">{g.hint}</div>
          </div>
        </label>
      ))}
      <div className="text-[10px] text-muted-foreground mt-3 pt-3 border-t border-border">
        These settings apply to every task you watch. Changes are saved automatically.
      </div>
    </div>
  );
}

